/**
 * SmartScheduleStep
 *
 * Step 6 of the Bulk Content Planner.
 * Auto-schedules generated creatives with optimal posting times.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    Calendar,
    Clock,
    CheckCircle,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    Instagram,
    Facebook,
    Linkedin,
    Image,
    Video,
    Layers,
    Sparkles,
    Send,
    Edit2,
    X,
    Check,
    GripVertical,
    ArrowRight,
    LayoutGrid
} from 'lucide-react';
import {
    distributePostsInDateRange,
    getUserTimezone,
    formatTimeForTimezone,
    formatDateForDisplay,
    getTimeSlotQuality,
    OPTIMAL_POSTING_TIMES
} from '../../services/postingTimeService';
import DeploymentPathSelector from './DeploymentPathSelector';

// Platform icons mapping
const PLATFORM_ICONS = {
    instagram: Instagram,
    facebook: Facebook,
    linkedin: Linkedin,
    tiktok: () => <span style={{ fontSize: '12px', fontWeight: 600 }}>TT</span>,
    twitter: () => <span style={{ fontSize: '12px', fontWeight: 600 }}>X</span>,
    google: () => <span style={{ fontSize: '12px', fontWeight: 600 }}>G</span>
};

const OUTPUT_TYPE_ICONS = {
    image: Image,
    video: Video,
    carousel: Layers
};

const QUALITY_COLORS = {
    optimal: { bg: 'var(--success-100)', border: 'var(--success-300)', text: 'var(--success-700)' },
    good: { bg: 'var(--warning-100)', border: 'var(--warning-300)', text: 'var(--warning-700)' },
    avoid: { bg: 'var(--error-100)', border: 'var(--error-300)', text: 'var(--error-700)' }
};

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SmartScheduleStep = ({
    briefs,
    startDate,
    endDate,
    platforms,
    onSchedule,
    onBack,
    onScheduledPostsChange,
    onDeployPaid  // Callback to open DeployToFunnelModal for paid content
}) => {
    const [scheduledPosts, setScheduledPosts] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [editingPost, setEditingPost] = useState(null);
    const [isScheduling, setIsScheduling] = useState(false);
    const [isScheduled, setIsScheduled] = useState(false);  // Track if scheduling completed
    const [currentViewMonth, setCurrentViewMonth] = useState(null);
    const [draggedPost, setDraggedPost] = useState(null);  // Track dragged post
    const [dragOverDate, setDragOverDate] = useState(null);  // Track drag target
    const [scheduleMode, setScheduleMode] = useState('even');  // 'even' or 'sequential'
    const [includeWeekends, setIncludeWeekends] = useState(true);
    const timezone = getUserTimezone();

    // Count organic vs paid content
    const organicBriefs = useMemo(() =>
        briefs.filter(b => b.contentType !== 'paid'),
        [briefs]
    );
    const paidBriefs = useMemo(() =>
        briefs.filter(b => b.contentType === 'paid'),
        [briefs]
    );

    // Today's date for comparison
    const today = useMemo(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    }, []);

    // Wizard-selected dates for AUTO-DISTRIBUTION only
    const parsedStartDate = useMemo(() => {
        if (!startDate) {
            // Default to today if no start date specified
            return today;
        }
        // Parse date string and normalize to local midnight
        const [year, month, day] = startDate.split('-').map(Number);
        const wizardStartDate = new Date(year, month - 1, day, 0, 0, 0, 0);
        // If wizard start date is in the past, use today instead
        return wizardStartDate < today ? today : wizardStartDate;
    }, [startDate, today]);

    const parsedEndDate = useMemo(() => {
        if (!endDate) {
            // Default to 1 month from start date if no end date specified
            return new Date(parsedStartDate.getFullYear(), parsedStartDate.getMonth() + 1, parsedStartDate.getDate());
        }
        // Parse date string and normalize to local midnight
        const [year, month, day] = endDate.split('-').map(Number);
        return new Date(year, month - 1, day, 23, 59, 59, 999);
    }, [endDate, parsedStartDate]);

    // Initialize current view month to today's month
    useEffect(() => {
        if (currentViewMonth === null) {
            const now = new Date();
            setCurrentViewMonth({
                month: now.getMonth(),
                year: now.getFullYear()
            });
        }
    }, [currentViewMonth]);

    // Filter to briefs that have been generated
    const generatedBriefs = useMemo(() =>
        briefs.filter(b => b.status === 'generated' || b.generatedCreativeId),
        [briefs]
    );

    // Auto-distribute posts on mount and when scheduling options change
    useEffect(() => {
        if (generatedBriefs.length > 0) {
            const distributed = distributePostsInDateRange(
                generatedBriefs,
                parsedStartDate,
                parsedEndDate,
                platforms,
                timezone,
                { mode: scheduleMode, includeWeekends }
            );
            setScheduledPosts(distributed);
        }
    }, [generatedBriefs, parsedStartDate, parsedEndDate, platforms, timezone, scheduleMode, includeWeekends]);

    // Notify parent of scheduledPosts changes (for Deploy to Funnel)
    useEffect(() => {
        onScheduledPostsChange?.(scheduledPosts);
    }, [scheduledPosts, onScheduledPostsChange]);

    // Get current month/year for display (default to current month)
    const displayMonth = currentViewMonth?.month ?? new Date().getMonth();
    const displayYear = currentViewMonth?.year ?? new Date().getFullYear();

    // Navigate months
    const navigateMonth = (direction) => {
        setCurrentViewMonth(prev => {
            let newMonth = (prev?.month ?? parsedStartDate.getMonth()) + direction;
            let newYear = prev?.year ?? parsedStartDate.getFullYear();
            if (newMonth < 0) {
                newMonth = 11;
                newYear--;
            } else if (newMonth > 11) {
                newMonth = 0;
                newYear++;
            }
            return { month: newMonth, year: newYear };
        });
    };

    // Build calendar grid
    const calendarDays = useMemo(() => {
        const firstDay = new Date(displayYear, displayMonth, 1);
        const lastDay = new Date(displayYear, displayMonth + 1, 0);
        const startPadding = firstDay.getDay();
        const daysInMonth = lastDay.getDate();

        // Normalize today to midnight for accurate day-level comparison
        const todayMidnight = new Date();
        todayMidnight.setHours(0, 0, 0, 0);

        const days = [];

        // Add padding days from previous month
        for (let i = 0; i < startPadding; i++) {
            days.push({ type: 'padding', date: null });
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(displayYear, displayMonth, day);
            const postsOnDay = scheduledPosts.filter(p => {
                const postDate = p.date instanceof Date ? p.date : new Date(p.date);
                return postDate.getDate() === day &&
                    postDate.getMonth() === displayMonth &&
                    postDate.getFullYear() === displayYear;
            });

            // Normalize date to midnight for comparison
            const dateMidnight = new Date(date);
            dateMidnight.setHours(0, 0, 0, 0);

            const isToday = dateMidnight.getTime() === todayMidnight.getTime();
            const isPast = dateMidnight.getTime() < todayMidnight.getTime();

            // Check if date is within the wizard-selected range (for visual indication)
            const isInWizardRange = dateMidnight >= parsedStartDate && dateMidnight <= parsedEndDate;

            // Manual scheduling allowed from today onwards (any future date)
            const isSchedulable = !isPast || isToday;

            days.push({
                type: 'day',
                date,
                day,
                posts: postsOnDay,
                isToday,
                isPast,
                isInRange: isSchedulable,  // Can drop/select on any future date
                isInWizardRange  // Visual indicator for wizard-selected range
            });
        }

        return days;
    }, [displayYear, displayMonth, scheduledPosts, parsedStartDate, parsedEndDate]);

    // Get posts for selected date
    const postsForSelectedDate = useMemo(() => {
        if (!selectedDate) return [];
        return scheduledPosts.filter(p => {
            const postDate = p.date instanceof Date ? p.date : new Date(p.date);
            return isSameDay(postDate, selectedDate);
        });
    }, [selectedDate, scheduledPosts]);

    // Helper to check same day
    function isSameDay(date1, date2) {
        return date1.getDate() === date2.getDate() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear();
    }

    // Update post time
    const updatePostTime = (postId, newTime) => {
        setScheduledPosts(prev => prev.map(post => {
            if (post.briefId !== postId) return post;

            const [hours, minutes] = newTime.split(':').map(Number);
            const newDateTime = new Date(post.date);
            newDateTime.setHours(hours, minutes, 0, 0);

            return {
                ...post,
                time: newTime,
                dateTime: newDateTime,
                quality: getTimeSlotQuality(newTime, post.date, post.platforms),
                suggestedBy: 'manual'
            };
        }));
        setEditingPost(null);
    };

    // Remove post from schedule
    const removePost = (postId) => {
        setScheduledPosts(prev => prev.filter(p => p.briefId !== postId));
    };

    // Drag and drop handlers
    const handleDragStart = (e, post) => {
        setDraggedPost(post);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', post.briefId);
    };

    const handleDragEnd = () => {
        setDraggedPost(null);
        setDragOverDate(null);
    };

    const handleDragOver = (e, date) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (date && !isSameDay(date, dragOverDate)) {
            setDragOverDate(date);
        }
    };

    const handleDragLeave = () => {
        setDragOverDate(null);
    };

    const handleDrop = (e, targetDate) => {
        e.preventDefault();
        if (!draggedPost || !targetDate) return;

        // Move post to new date while keeping the same time
        setScheduledPosts(prev => prev.map(post => {
            if (post.briefId !== draggedPost.briefId) return post;

            const [hours, minutes] = post.time.split(':').map(Number);
            const newDateTime = new Date(targetDate);
            newDateTime.setHours(hours, minutes, 0, 0);

            return {
                ...post,
                date: targetDate,
                dateTime: newDateTime,
                quality: getTimeSlotQuality(post.time, targetDate, post.platforms),
                suggestedBy: 'manual'
            };
        }));

        setDraggedPost(null);
        setDragOverDate(null);
        setSelectedDate(targetDate);  // Select the target date to show the moved post
    };

    // Handle final schedule
    const handleScheduleAll = async () => {
        setIsScheduling(true);
        try {
            await onSchedule?.(scheduledPosts);
            setIsScheduled(true);  // Mark as scheduled to show deployment options
        } catch (error) {
            console.error('Scheduling failed:', error);
        } finally {
            setIsScheduling(false);
        }
    };

    // Stats
    const optimalCount = scheduledPosts.filter(p => p.quality === 'optimal').length;
    const goodCount = scheduledPosts.filter(p => p.quality === 'good').length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Header */}
            <div className="card" style={{ padding: 'var(--space-4)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Sparkles size={20} style={{ color: 'var(--color-primary)' }} />
                            Smart Schedule
                        </h3>
                        <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                            {scheduledPosts.length} posts auto-scheduled • Drag posts to reschedule
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-1) var(--space-2)',
                            background: 'var(--success-100)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: 'var(--success-500)'
                            }} />
                            <span style={{ color: 'var(--success-700)' }}>{optimalCount} optimal</span>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-1) var(--space-2)',
                            background: 'var(--warning-100)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: 'var(--warning-500)'
                            }} />
                            <span style={{ color: 'var(--warning-700)' }}>{goodCount} good</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scheduling Options */}
            <div className="card" style={{ padding: 'var(--space-3) var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                {/* Distribution Mode Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                        Distribution:
                    </span>
                    <div style={{
                        display: 'flex',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-border)',
                        overflow: 'hidden'
                    }}>
                        <button
                            onClick={() => setScheduleMode('even')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-1) var(--space-3)',
                                fontSize: 'var(--font-size-sm)',
                                border: 'none',
                                cursor: 'pointer',
                                background: scheduleMode === 'even' ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
                                color: scheduleMode === 'even' ? 'var(--color-primary-foreground)' : 'var(--color-text-secondary)',
                                fontWeight: scheduleMode === 'even' ? 600 : 400,
                                transition: 'all 0.15s ease'
                            }}
                        >
                            <LayoutGrid size={14} />
                            Spread evenly
                        </button>
                        <button
                            onClick={() => setScheduleMode('sequential')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-1) var(--space-3)',
                                fontSize: 'var(--font-size-sm)',
                                border: 'none',
                                borderLeft: '1px solid var(--color-border)',
                                cursor: 'pointer',
                                background: scheduleMode === 'sequential' ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
                                color: scheduleMode === 'sequential' ? 'var(--color-primary-foreground)' : 'var(--color-text-secondary)',
                                fontWeight: scheduleMode === 'sequential' ? 600 : 400,
                                transition: 'all 0.15s ease'
                            }}
                        >
                            <ArrowRight size={14} />
                            Sequential
                        </button>
                    </div>
                </div>

                {/* Divider */}
                <div style={{ width: '1px', height: '24px', background: 'var(--color-border)' }} />

                {/* Weekend Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                        Weekends:
                    </span>
                    <button
                        onClick={() => setIncludeWeekends(!includeWeekends)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            padding: 'var(--space-1) var(--space-3)',
                            fontSize: 'var(--font-size-sm)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            cursor: 'pointer',
                            background: includeWeekends ? 'var(--success-100)' : 'var(--color-bg-secondary)',
                            color: includeWeekends ? 'var(--success-700)' : 'var(--color-text-secondary)',
                            fontWeight: 500,
                            transition: 'all 0.15s ease'
                        }}
                    >
                        <div style={{
                            width: '32px',
                            height: '18px',
                            borderRadius: '9px',
                            background: includeWeekends ? 'var(--success-500)' : 'var(--color-text-tertiary)',
                            position: 'relative',
                            transition: 'background 0.2s ease'
                        }}>
                            <div style={{
                                width: '14px',
                                height: '14px',
                                borderRadius: '50%',
                                background: 'white',
                                position: 'absolute',
                                top: '2px',
                                left: includeWeekends ? '16px' : '2px',
                                transition: 'left 0.2s ease'
                            }} />
                        </div>
                        {includeWeekends ? 'Included' : 'Excluded'}
                    </button>
                </div>

                {/* Mode Description */}
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginLeft: 'auto' }}>
                    {scheduleMode === 'even'
                        ? `Posts spread evenly across ${includeWeekends ? 'all' : 'week'} days`
                        : `One post per ${includeWeekends ? 'day' : 'weekday'}, starting from first date`
                    }
                </div>
            </div>

            {/* Calendar + Detail Split */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-4)' }}>
                {/* Calendar */}
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    {/* Month Header with Navigation */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <button
                            onClick={() => navigateMonth(-1)}
                            className="btn btn-ghost"
                            style={{ padding: 'var(--space-2)' }}
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                            {MONTH_NAMES[displayMonth]} {displayYear}
                        </span>
                        <button
                            onClick={() => navigateMonth(1)}
                            className="btn btn-ghost"
                            style={{ padding: 'var(--space-2)' }}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>

                    {/* Day Headers */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px',
                        marginBottom: 'var(--space-2)'
                    }}>
                        {DAY_NAMES.map(day => (
                            <div key={day} style={{
                                textAlign: 'center',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 500,
                                color: 'var(--color-text-secondary)',
                                padding: 'var(--space-1)'
                            }}>
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Calendar Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px'
                    }}>
                        {calendarDays.map((dayData, index) => (
                            <CalendarDay
                                key={index}
                                dayData={dayData}
                                isSelected={selectedDate && dayData.date && isSameDay(selectedDate, dayData.date)}
                                onSelect={() => dayData.date && setSelectedDate(dayData.date)}
                                isDragOver={dragOverDate && dayData.date && isSameDay(dragOverDate, dayData.date)}
                                onDragOver={(e) => handleDragOver(e, dayData.date)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, dayData.date)}
                            />
                        ))}
                    </div>

                    {/* Legend */}
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--space-3)',
                        marginTop: 'var(--space-4)',
                        paddingTop: 'var(--space-3)',
                        borderTop: '1px solid var(--color-border)',
                        justifyContent: 'center'
                    }}>
                        {/* Time quality legend */}
                        {[
                            { color: 'var(--success-500)', label: 'Optimal' },
                            { color: 'var(--warning-500)', label: 'Good' },
                            { color: 'var(--error-500)', label: 'Avoid' }
                        ].map(item => (
                            <div key={item.label} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: item.color
                                }} />
                                {item.label}
                            </div>
                        ))}
                        {/* Date range legend */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)',
                            marginLeft: 'var(--space-2)',
                            paddingLeft: 'var(--space-2)',
                            borderLeft: '1px solid var(--color-border)'
                        }}>
                            <div style={{
                                width: '14px',
                                height: '14px',
                                borderRadius: '2px',
                                background: 'var(--success-50)',
                                border: '1px solid var(--success-300)'
                            }} />
                            Plan range
                        </div>
                    </div>
                </div>

                {/* Selected Day Detail */}
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    {selectedDate ? (
                        <>
                            <h4 style={{ margin: '0 0 var(--space-3) 0' }}>
                                {formatDateForDisplay(selectedDate, timezone)}
                            </h4>

                            {postsForSelectedDate.length === 0 ? (
                                <div style={{
                                    padding: 'var(--space-6)',
                                    textAlign: 'center',
                                    color: 'var(--color-text-tertiary)'
                                }}>
                                    No posts scheduled for this day
                                </div>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 'var(--space-2)'
                                }}>
                                    {postsForSelectedDate.map(post => (
                                        <ScheduledPostCard
                                            key={post.briefId}
                                            post={post}
                                            isEditing={editingPost === post.briefId}
                                            onEdit={() => setEditingPost(post.briefId)}
                                            onSave={(time) => updatePostTime(post.briefId, time)}
                                            onCancel={() => setEditingPost(null)}
                                            onRemove={() => removePost(post.briefId)}
                                            onDragStart={(e) => handleDragStart(e, post)}
                                            onDragEnd={handleDragEnd}
                                            isDragging={draggedPost?.briefId === post.briefId}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{
                            padding: 'var(--space-6)',
                            textAlign: 'center',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            <Calendar size={32} style={{ marginBottom: 'var(--space-2)', opacity: 0.5 }} />
                            <p>Select a day to view scheduled posts</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Actions */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 'var(--space-4)',
                borderTop: '1px solid var(--color-border)'
            }}>
                <button onClick={onBack} className="btn btn-ghost">
                    Back
                </button>

                <button
                    onClick={handleScheduleAll}
                    disabled={scheduledPosts.length === 0 || isScheduling}
                    className="btn btn-primary"
                >
                    {isScheduling ? (
                        <>
                            <span className="spin">⏳</span>
                            Scheduling...
                        </>
                    ) : (
                        <>
                            <Send size={16} />
                            Schedule All ({scheduledPosts.length} posts)
                        </>
                    )}
                </button>
            </div>

            {/* Deployment Path Selector - shows after scheduling */}
            {isScheduled && (
                <DeploymentPathSelector
                    organicCount={organicBriefs.length}
                    paidCount={paidBriefs.length}
                    organicScheduled={true}
                    onPaidDeploy={onDeployPaid}
                    isLoading={isScheduling}
                />
            )}
        </div>
    );
};

// Calendar Day Cell
const CalendarDay = ({ dayData, isSelected, onSelect, isDragOver, onDragOver, onDragLeave, onDrop }) => {
    if (dayData.type === 'padding') {
        return <div style={{ aspectRatio: '1', background: 'transparent' }} />;
    }

    const { day, posts, isToday, isPast, isInRange = true, isInWizardRange = false } = dayData;

    // Determine if day is interactive (can select/drop)
    // Past days are disabled, today and future are enabled
    const isDisabled = isPast && !isToday;

    // Allow drop on any schedulable day (today or future)
    const canDrop = isInRange;

    return (
        <div
            onClick={!isDisabled ? onSelect : undefined}
            onDragOver={canDrop ? onDragOver : undefined}
            onDragLeave={canDrop ? onDragLeave : undefined}
            onDrop={canDrop ? onDrop : undefined}
            style={{
                aspectRatio: '1',
                padding: 'var(--space-1)',
                background: isDragOver
                    ? 'var(--color-primary-alpha)'
                    : isSelected
                        ? 'var(--color-primary-alpha)'
                        : isPast
                            ? 'var(--color-bg-tertiary)'
                            : isInWizardRange
                                ? 'var(--success-50)'  // Highlight wizard range
                                : 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-sm)',
                cursor: isDisabled ? 'default' : 'pointer',
                border: isDragOver
                    ? '2px dashed var(--color-primary)'
                    : isSelected
                        ? '2px solid var(--color-primary)'
                        : isToday
                            ? '2px solid var(--info-400)'
                            : isInWizardRange
                                ? '1px solid var(--success-300)'  // Subtle border for wizard range
                                : '1px solid var(--color-border)',
                display: 'flex',
                flexDirection: 'column',
                opacity: isPast && !isToday ? 0.4 : 1,
                transition: 'all 0.15s ease',
                transform: isDragOver ? 'scale(1.02)' : 'none'
            }}
        >
            <div style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: isToday ? 600 : 400,
                color: isToday ? 'var(--info-600)' : 'var(--color-text-primary)'
            }}>
                {day}
            </div>

            {posts.length > 0 && (
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    gap: '2px'
                }}>
                    {posts.slice(0, 3).map((post, i) => (
                        <div
                            key={i}
                            style={{
                                height: '4px',
                                borderRadius: '2px',
                                background: post.quality === 'optimal'
                                    ? 'var(--success-500)'
                                    : post.quality === 'good'
                                        ? 'var(--warning-500)'
                                        : 'var(--error-500)'
                            }}
                        />
                    ))}
                    {posts.length > 3 && (
                        <div style={{
                            fontSize: '9px',
                            color: 'var(--color-text-tertiary)',
                            textAlign: 'center'
                        }}>
                            +{posts.length - 3}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Scheduled Post Card
const ScheduledPostCard = ({ post, isEditing, onEdit, onSave, onCancel, onRemove, onDragStart, onDragEnd, isDragging }) => {
    const [editTime, setEditTime] = useState(post.time);
    const [isExpanded, setIsExpanded] = useState(false);
    const TypeIcon = OUTPUT_TYPE_ICONS[post.brief?.outputType] || Image;
    const qualityColors = QUALITY_COLORS[post.quality] || QUALITY_COLORS.good;

    const briefData = post.brief?.brief || {};
    const imageUrl = post.brief?.generatedImageUrl || post.brief?.generatedThumbnailUrl;
    const caption = briefData.caption || briefData.copy || briefData.selectedHeadline;
    const funnelStage = post.brief?.funnelStage;
    const pillarName = post.brief?.pillarName;
    const cta = briefData.cta;
    const marketingAngle = briefData.marketingAngle;

    return (
        <div
            draggable={!isEditing}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            style={{
                background: qualityColors.bg,
                border: `1px solid ${qualityColors.border}`,
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                cursor: isEditing ? 'default' : 'grab',
                opacity: isDragging ? 0.5 : 1,
                transition: 'opacity 0.2s ease'
            }}
        >
            {/* Main Content Row */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-3)',
                padding: 'var(--space-3)',
                alignItems: 'flex-start'
            }}>
                {/* Drag Handle */}
                {!isEditing && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--color-text-tertiary)',
                            cursor: 'grab',
                            padding: 'var(--space-1) 0',
                            flexShrink: 0
                        }}
                        title="Drag to reschedule"
                    >
                        <GripVertical size={16} />
                    </div>
                )}

                {/* Image Thumbnail */}
                <div
                    style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: 'var(--radius-sm)',
                        overflow: 'hidden',
                        flexShrink: 0,
                        background: 'var(--color-bg-tertiary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt="Creative preview"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    ) : (
                        <TypeIcon size={24} style={{ color: 'var(--color-text-tertiary)' }} />
                    )}
                </div>

                {/* Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Title Row */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-1)'
                    }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: '2px'
                            }}>
                                <TypeIcon size={12} style={{ color: qualityColors.text, flexShrink: 0 }} />
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 600,
                                    color: qualityColors.text,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {briefData.selectedHeadline || `Brief #${post.brief?.sequence}`}
                                </span>
                            </div>

                            {/* Tags Row */}
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                                marginBottom: 'var(--space-1)'
                            }}>
                                {funnelStage && (
                                    <span style={{
                                        fontSize: '10px',
                                        padding: '1px 6px',
                                        borderRadius: 'var(--radius-full)',
                                        background: funnelStage === 'TOFU' ? 'var(--info-100)'
                                            : funnelStage === 'MOFU' ? 'var(--warning-100)'
                                            : 'var(--success-100)',
                                        color: funnelStage === 'TOFU' ? 'var(--info-700)'
                                            : funnelStage === 'MOFU' ? 'var(--warning-700)'
                                            : 'var(--success-700)',
                                        fontWeight: 500
                                    }}>
                                        {funnelStage}
                                    </span>
                                )}
                                {pillarName && (
                                    <span style={{
                                        fontSize: '10px',
                                        padding: '1px 6px',
                                        borderRadius: 'var(--radius-full)',
                                        background: 'var(--color-bg-tertiary)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        {pillarName}
                                    </span>
                                )}
                                {post.brief?.abTest?.isAbTest && (
                                    <span style={{
                                        fontSize: '10px',
                                        padding: '1px 6px',
                                        borderRadius: 'var(--radius-full)',
                                        background: 'var(--purple-100, #f3e8ff)',
                                        color: 'var(--purple-700, #7c3aed)',
                                        fontWeight: 600
                                    }}>
                                        A/B {post.brief.abTest.isVariantA ? 'A' : 'B'}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: 'var(--space-1)', flexShrink: 0 }}>
                            {isEditing ? (
                                <>
                                    <button
                                        onClick={() => onSave(editTime)}
                                        style={{
                                            padding: '4px',
                                            background: 'var(--success-100)',
                                            border: 'none',
                                            borderRadius: 'var(--radius-sm)',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Check size={12} style={{ color: 'var(--success-600)' }} />
                                    </button>
                                    <button
                                        onClick={onCancel}
                                        style={{
                                            padding: '4px',
                                            background: 'var(--color-bg-tertiary)',
                                            border: 'none',
                                            borderRadius: 'var(--radius-sm)',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <X size={12} />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={onEdit}
                                        style={{
                                            padding: '4px',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            opacity: 0.6
                                        }}
                                        title="Edit time"
                                    >
                                        <Edit2 size={12} />
                                    </button>
                                    <button
                                        onClick={onRemove}
                                        style={{
                                            padding: '4px',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            opacity: 0.6
                                        }}
                                        title="Remove from schedule"
                                    >
                                        <X size={12} />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Time & Platforms Row */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        <Clock size={12} />
                        {isEditing ? (
                            <input
                                type="time"
                                value={editTime}
                                onChange={(e) => setEditTime(e.target.value)}
                                className="input"
                                style={{
                                    padding: '2px 4px',
                                    fontSize: 'var(--font-size-xs)',
                                    width: 'auto'
                                }}
                            />
                        ) : (
                            <span style={{ fontWeight: 500 }}>{post.time}</span>
                        )}
                        <span style={{ color: 'var(--color-border)' }}>|</span>
                        {post.platforms?.map(platform => {
                            const PlatformIcon = PLATFORM_ICONS[platform];
                            return PlatformIcon ? (
                                <PlatformIcon key={platform} size={12} />
                            ) : null;
                        })}
                    </div>
                </div>
            </div>

            {/* Expandable Caption Section */}
            {caption && (
                <div
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{
                        padding: 'var(--space-2) var(--space-3)',
                        borderTop: `1px solid ${qualityColors.border}`,
                        background: 'rgba(0,0,0,0.03)',
                        cursor: 'pointer'
                    }}
                >
                    <div style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        display: '-webkit-box',
                        WebkitLineClamp: isExpanded ? 'unset' : 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: isExpanded ? 'visible' : 'hidden',
                        lineHeight: 1.4
                    }}>
                        {caption}
                    </div>
                    {cta && (
                        <div style={{
                            marginTop: 'var(--space-1)',
                            fontSize: '10px',
                            color: 'var(--color-primary)',
                            fontWeight: 500
                        }}>
                            CTA: {cta}
                        </div>
                    )}
                    {marketingAngle && isExpanded && (
                        <div style={{
                            marginTop: 'var(--space-1)',
                            fontSize: '10px',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            Angle: {marketingAngle}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SmartScheduleStep;
